import { Component, Input, Output, EventEmitter, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

export interface RelativeDateOption {
  label: string;
  value: string;
  days?: number;
  months?: number;
  years?: number;
}

export interface DatePickerValue {
  mode: 'relative' | 'specific';
  value: any; // For relative: string (e.g., 'today', 'custom'), for specific: Date object
  customDays?: number;
  customMonths?: number;
  customYears?: number;
}

@Component({
  selector: 'bhub-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    }
  ],
  standalone: false
})
export class DatePickerComponent implements OnInit, ControlValueAccessor {
  @Input() label: string = 'Select Date';
  @Input() placeholder: string = 'Choose date...';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Output() dateChange = new EventEmitter<DatePickerValue>();

  value: DatePickerValue = {
    mode: 'relative',
    value: 'today'
  };

  relativeDateOptions: RelativeDateOption[] = [
    { label: 'Today', value: 'today', days: 0 },
    { label: 'Yesterday', value: 'yesterday', days: -1 },
    { label: 'Tomorrow', value: 'tomorrow', days: 1 },
    { label: 'Last 7 days', value: 'last7days', days: -7 },
    { label: 'Last 30 days', value: 'last30days', days: -30 },
    { label: 'Last 90 days', value: 'last90days', days: -90 },
    { label: 'Last year', value: 'lastyear', years: -1 },
    { label: 'Next 7 days', value: 'next7days', days: 7 },
    { label: 'Next 30 days', value: 'next30days', days: 30 },
    { label: 'Next 90 days', value: 'next90days', days: 90 },
    { label: 'Next year', value: 'nextyear', years: 1 },
    { label: 'Custom...', value: 'custom' }
  ];

  customRelativeOptions = [
    { label: 'Days', value: 'days' },
    { label: 'Months', value: 'months' },
    { label: 'Years', value: 'years' }
  ];

  selectedCustomUnit: string = 'days';
  customValue: number = 1;

  private onChange = (value: DatePickerValue) => {};
  private onTouched = () => {};

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    // Initialize with today if no value is set
    if (!this.value.value && this.value.mode === 'relative') {
      this.value.value = 'today';
      this.onChange(this.value);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: DatePickerValue): void {
    if (value) {
      // Ensure value is a Date object if it's a string and mode is specific
      if (value.mode === 'specific' && value.value && typeof value.value === 'string') {
        value.value = new Date(value.value);
      }

      this.value = value;

      // Update custom values if in custom mode
      if (value.mode === 'relative' && value.value === 'custom') {
        if (value.customDays !== undefined) {
          this.selectedCustomUnit = 'days';
          this.customValue = value.customDays;
        } else if (value.customMonths !== undefined) {
          this.selectedCustomUnit = 'months';
          this.customValue = value.customMonths;
        } else if (value.customYears !== undefined) {
          this.selectedCustomUnit = 'years';
          this.customValue = value.customYears;
        }
      }
    } else {
      this.value = { mode: 'relative', value: 'today' };
    }
  }

  registerOnChange(fn: (value: DatePickerValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onModeChange(): void {
    if (this.value.mode === 'relative') {
      // Clear specific date when switching to relative mode
      this.value.value = undefined;
      // Initialize relative value if not set
      if (!this.value.value) {
        this.value.value = 'today';
      }
    } else if (this.value.mode === 'specific') {
      // Clear relative date values when switching to specific mode
      this.value.value = undefined;
      this.value.customDays = undefined;
      this.value.customMonths = undefined;
      this.value.customYears = undefined;
      // Clear custom values
      this.customValue = 1;
      this.selectedCustomUnit = 'days';
    }
    this.emitChange();
  }

  onRelativeValueChange(): void {
    if (this.value.value === 'custom') {
      this.value.customDays = this.selectedCustomUnit === 'days' ? this.customValue : undefined;
      this.value.customMonths = this.selectedCustomUnit === 'months' ? this.customValue : undefined;
      this.value.customYears = this.selectedCustomUnit === 'years' ? this.customValue : undefined;
    } else {
      // Clear custom values when using predefined options
      this.value.customDays = undefined;
      this.value.customMonths = undefined;
      this.value.customYears = undefined;
    }
    this.emitChange();
  }

  onCustomValueChange(): void {
    this.onRelativeValueChange();
  }

  onCustomUnitChange(): void {
    this.onRelativeValueChange();
  }

  onSpecificDateChange(event: MatDatepickerInputEvent<Date>): void {
    this.value.value = event.value || undefined;
    this.emitChange();
  }

  getDisplayValue(): string {
    if (this.value.mode === 'relative') {
      if (this.value.value === 'custom') {
        const unit = this.selectedCustomUnit;
        const value = this.customValue;
        const direction = value >= 0 ? 'from now' : 'ago';
        return `${Math.abs(value)} ${unit} ${direction}`;
      } else {
        const option = this.relativeDateOptions.find(opt => opt.value === this.value.value);
        return option ? option.label : 'Select relative date';
      }
    } else {
      if (this.value.value) {
        // Ensure specificDate is a Date object before calling toLocaleDateString
        const date = this.value.value instanceof Date ? this.value.value : new Date(this.value.value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      }
      return 'Select specific date';
    }
  }

  getCalculatedDate(): Date | null {
    if (this.value.mode === 'relative') {
      const today = new Date();

      if (this.value.value === 'custom') {
        const result = new Date(today);
        if (this.value.customDays) {
          result.setDate(result.getDate() + this.value.customDays);
        }
        if (this.value.customMonths) {
          result.setMonth(result.getMonth() + this.value.customMonths);
        }
        if (this.value.customYears) {
          result.setFullYear(result.getFullYear() + this.value.customYears);
        }
        return result;
      } else {
        const option = this.relativeDateOptions.find(opt => opt.value === this.value.value);
        if (option) {
          const result = new Date(today);
          if (option.days) {
            result.setDate(result.getDate() + option.days);
          }
          if (option.months) {
            result.setMonth(result.getMonth() + option.months);
          }
          if (option.years) {
            result.setFullYear(result.getFullYear() + option.years);
          }
          return result;
        }
      }
    } else {
      if (this.value.value) {
        // Ensure specificDate is a Date object
        const date = this.value.value instanceof Date ? this.value.value : new Date(this.value.value);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
      return null;
    }

    return null;
  }

  private emitChange(): void {
    this.onChange(this.value);
    this.onTouched();
    this.dateChange.emit(this.value);
  }

  isCustomMode(): boolean {
    return this.value.mode === 'relative' && this.value.value === 'custom';
  }
}
